import {
  FocusContext,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation";
import { useCallback, useLayoutEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getCMSImageURL, getOptimizedImageForType } from "../../utils/cms.utils";
import OptimizedImage from "../common/OptimizedImage";
import ContentTag from "../../utils/tag";
import classNames from "classnames";
import ViewMoreCard from "../common/ViewMoreCard";
import { setAutoResumeCookie } from "../../utils/autoResumeCookie";
import { useInputMode } from "../../utils/hooks/useInputMode";
import { useContentImpression } from "../../utils/hooks/useContentImpression";
import LiveStartCountdown from "../common/LiveStartCountdown";
import {
  getDisplayYear,
  getFutureLiveStartTimestamp,
  isLiveContent,
} from "../../utils/liveStartCountdown";
import {
  focusHomeMenu,
  resetRowTransformAfterBlur,
  resetRowTransformIfUnfocused,
  syncFocusedRowTransform,
} from "./focusHelpers";
import NextPlayGuideHelper from "../../utils/nextPlayGuideHelper";

const aspectGuide = {
  movie: "legacy-showcase-card-poster",
  searchbox: "legacy-showcase-card-poster",
  series: "legacy-showcase-card-poster",
  program: "legacy-showcase-card-wide",
  sport: "legacy-showcase-card-wide",
  music: "legacy-showcase-card-square",
  animation: "legacy-showcase-card-square",
  artist: "legacy-showcase-card-artist-size",
};

const MovieItem = ({ item = {}, onFocus, onBlur, shapeType, isFirst, focusKey, elapsedTime = 0, totalTime = 0, seasonNumber = null, episodeNumber = null, isContinueWatch = false, containerKey = "", position = 0, nextPlayGuide = null }) => {
  const navigate = useNavigate();
  const {
    contentPhotos,
    title,
    year,
    slug,
    tags,
    image,
    farsiName,
    kind,
    contentType,
  } = item || {};

  // Get navigation URL - for continue-watch, go directly to player
  const getNavigationUrl = () => {
    if (shapeType === "artist") {
      return `/artists/${slug}`;
    }

    if (isContinueWatch) {
      // Navigate directly to player for continue-watch items
      if (seasonNumber && episodeNumber) {
        // Series: /watch/{slug}/{season}/{episode}
        return `/watch/${slug}/${seasonNumber}/${episodeNumber}`;
      }
      // Movie: /watch/{slug}
      return `/watch/${slug}`;
    }

    // Normal navigation to content page
    return `/${contentType?.kind || kind}/${slug}`;
  };

  // Handle navigation with cookie for continue-watch
  const handleNavigation = () => {
    if (isContinueWatch) {
      // Set encrypted cookie before navigation
      setAutoResumeCookie({
        slug,
        season: seasonNumber ? String(seasonNumber) : null,
        episode: episodeNumber ? String(episodeNumber) : null,
      });
    }
    const navigationUrl = getNavigationUrl();
    const contentKind = contentType?.kind || kind;
    if (!isContinueWatch && contentKind === "music" && nextPlayGuide) {
      navigate(navigationUrl, {
        state: NextPlayGuideHelper.withGuideNavigationState(null, nextPlayGuide),
      });
    } else {
      navigate(navigationUrl);
    }
  };

  const { focused, ref, focusSelf } = useFocusable({
    focusKey,
    onFocus,
    onBlur,
    onEnterPress: handleNavigation,
    onArrowPress: (direction) => {
      if (direction === "right" && isFirst) {
        return focusHomeMenu();
      }
      return true;
    },
  });
  useContentImpression(ref, item, {
    surface: "home",
    source: "showcase",
    containerKey: containerKey,
    position: position,
  });

  const { setMouseFocus } = useInputMode();
  const handleMouseEnter = useCallback(() => {
    setMouseFocus(true);
    focusSelf();
  }, [focusSelf, setMouseFocus]);

  // Get optimized image with LQIP and WebP support (w=200, q=90)
  const getImageData = () => {
    if (shapeType === "artist") {
      // Artist images may not have LQIP/WebP structure
      return {
        src: getCMSImageURL(image, { width: 200, quality: 90, format: 'jpeg' }),
      };
    } else {
      return getOptimizedImageForType("verticalCover", contentPhotos, "TV", { width: 200, quality: 90, format: 'jpeg' });
    }
  };

  const imageData = getImageData();
  const liveStartTimestamp = isLiveContent(item) ? getFutureLiveStartTimestamp(item) : null;
  const displayYear = getDisplayYear(year);

  // Calculate progress percentage for continue-watch
  const progressPercent = totalTime > 0 ? Math.min((elapsedTime / totalTime) * 100, 100) : 0;
  const hasProgress = totalTime > 0 && elapsedTime > 0;

  // Check if this is a series with episode info
  const isSeries = contentType?.kind === "series" || kind === "series";
  const hasEpisodeInfo = seasonNumber && episodeNumber;

  return (
    <div className={shapeType === 'artist' ? "legacy-showcase-item legacy-showcase-item-artist" : "legacy-showcase-item"}>
      {isSeries && (
        <div className="legacy-showcase-series-decoration">
          <img src="/serial_pseudo.svg" className="legacy-showcase-series-image" alt="" />
        </div>
      )}
      <div
        ref={ref}
        onClick={handleNavigation}
        onMouseEnter={handleMouseEnter}
        className={classNames(
          "legacy-showcase-card",
          shapeType === "artist" && "legacy-showcase-card-artist",
          focused && "is-focused",
          "legacy-clickable",
          aspectGuide[shapeType]
        )}
      >
        <OptimizedImage
          src={imageData.src}
          alt={title}
          className="legacy-showcase-card-image"
          style={{ borderRadius: shapeType === 'artist' ? "50%" : "15px" }}
        />
        {tags?.[0] && <ContentTag label={tags?.[0]} />}

        {/* Season/Episode badge for series in continue-watch */}
        {hasEpisodeInfo && (
          <div className={"legacy-showcase-episode-badge" + (hasProgress ? " has-progress" : "")}>
            <span className="legacy-showcase-episode-text">فصل {seasonNumber} • قسمت {episodeNumber}</span>
          </div>
        )}

        {/* Progress bar for continue-watch */}
        {hasProgress && (
          <div className="legacy-showcase-progress">
            <div className="legacy-showcase-progress-fill" style={{ width: progressPercent + "%" }} />
          </div>
        )}

        {/* Focus overlay with border and content */}
        <div className="legacy-showcase-focus-overlay">
          <span className="legacy-showcase-title-text">{title}</span>
          {displayYear && <span className="legacy-showcase-year-text">{displayYear}</span>}
        </div>

        {liveStartTimestamp && shapeType !== "sport" && (
          <LiveStartCountdown
            startTimestamp={liveStartTimestamp}
            style={{
              position: "absolute",
              bottom: 8,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 15,
              justifyContent: "center",
            }}
          />
        )}
      </div>
      {liveStartTimestamp && shapeType === "sport" && (
        <LiveStartCountdown
          startTimestamp={liveStartTimestamp}
          size="sportCard"
          style={{ marginTop: 8, justifyContent: "center" }}
        />
      )}
      {farsiName && (
        <p className="legacy-showcase-artist-name">
          {farsiName}
        </p>
      )}
    </div>
  );
};

export const MoviesSlider = ({
  movies = [],
  onFocus,
  rowTitle,
  shapeType = "movie",
  id,
  index,
  hasMore = false,
  actionLink = null,
  onMoreTap = null,
  hasTopTranslate = false,
  isContinueWatch = false,
  contentKind,
  nextPlayGuide = null,
}) => {
  const itemFocusKeyPrefix = "movie_slider_item_" + id + "_";
  const { ref, focusKey } = useFocusable({
    isFocusBoundary: true,
    focusBoundaryDirections: ["left"],
    focusKey: "movie_slider_" + id,
    focusable: false,
    trackChildren: true,
    autoRestoreFocus: true,
    preferredChildFocusKey: itemFocusKeyPrefix + "0",
  });
  const scrollingRef = useRef(null);
  const innerRef = useRef(null);

  useLayoutEffect(() => {
    var delays = [0, 80, 220, 600, 1400, 2600];
    resetRowTransformIfUnfocused(innerRef);
    var timers = delays.map(function (delay) {
      return setTimeout(function () {
        resetRowTransformIfUnfocused(innerRef);
      }, delay);
    });

    return function () {
      timers.forEach(function (timer) {
        clearTimeout(timer);
      });
    };
  }, [movies.length]);

  // Determine if View More should be shown:
  // - Must have at least 5 items to justify a "view more" option
  // - AND (hasMore flag is true OR content length > 12)
  // - AND actionLink must exist for navigation
  const showViewMore = movies.length >= 5 && (hasMore || movies.length > 12) && actionLink;

  const { isMouseFocus, isMouseFocusRef } = useInputMode();

  const onAssetFocus = useCallback(
    (props) => {
      if (typeof onFocus === "function") onFocus(props);
      if (isMouseFocusRef.current || isMouseFocus) return; // Skip horizontal scroll on mouse hover
      syncFocusedRowTransform({
        innerRef,
        scrollerRef: scrollingRef,
        leftPadding: shapeType === "sport" ? 500 : 300,
      });
    },
    [scrollingRef, shapeType, isMouseFocusRef, isMouseFocus, onFocus]
  );
  const onAssetBlur = useCallback(
    () => {
      resetRowTransformAfterBlur(innerRef);
    },
    []
  );

  if (movies.length < 1) return null;

  return (
    <FocusContext.Provider value={focusKey}>
      <div
        ref={ref}
        className={classNames(
          "legacy-showcase-row",
          shapeType === "music" && "legacy-showcase-row-music",
          hasTopTranslate && "legacy-showcase-row-top-translated"
        )}
        tabIndex={index}
      >
        <h2 className="legacy-showcase-row-title">{rowTitle}</h2>
        <div
          className="legacy-showcase-row-scroller"
          ref={scrollingRef}
          style={{ paddingRight: 40 }}
        >
          <div className="legacy-showcase-row-content" ref={innerRef}>
            {movies?.map((item, itemIndex) => {
              // For continue-watch items, content has elapsedTime, totalTime at root level
              // after transformation in useHomeShowcaseData
              const content = item.content || item.artist;
              const itemElapsedTime = item.content?.elapsedTime || 0;
              const itemTotalTime = item.content?.totalTime || 0;
              const itemSeasonNumber = item.content?.seasonNumber || null;
              const itemEpisodeNumber = item.content?.episodeNumber || null;

              return (
                <MovieItem
                  item={content}
                  key={`ms_${item.content?.slug || item.artist?.slug}_${id}_${itemIndex}`}
                  onFocus={(e) => {
                    onAssetFocus(e);
                  }}
                  onBlur={onAssetBlur}
                  shapeType={shapeType}
                  isFirst={itemIndex === 0}
                  focusKey={itemFocusKeyPrefix + itemIndex}
                  elapsedTime={itemElapsedTime}
                  totalTime={itemTotalTime}
                  seasonNumber={itemSeasonNumber}
                  episodeNumber={itemEpisodeNumber}
                  isContinueWatch={isContinueWatch}
                  containerKey={id || rowTitle || ""}
                  position={itemIndex}
                  nextPlayGuide={nextPlayGuide}
                />
              );
            })}
            {/* View More Card - shown at end when hasMore is true and items >= 10 */}
            {showViewMore && (
              <div className="legacy-showcase-view-more">
                <ViewMoreCard
                  actionLink={actionLink}
                  onMoreTap={onMoreTap}
                  shapeType={shapeType}
                  contentKind={contentKind}
                  focusKey={`view_more_${id}`}
                  onFocus={(e) => {
                    onAssetFocus(e);
                  }}
                  onBlur={onAssetBlur}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </FocusContext.Provider>
  );
};
